/**
 * BrowserEventEmitter.ts
 * Implémentation d'EventEmitter compatible navigateur
 * Remplace le module Node.js "events" qui n'est pas disponible dans le navigateur
 */
import { logger } from './ProfessionalLogger';

export class BrowserEventEmitter {
  private events: Map<string, Array<(...args: unknown[]) => void>> = new Map();

  /**
   * Enregistre un écouteur pour un événement
   */
  on(event: string, listener: (...args: unknown[]) => void): this {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.events.get(event)!.push(listener);
    return this;
  }

  /**
   * Désenregistre un écouteur pour un événement
   */
  off(event: string, listener: (...args: unknown[]) => void): this {
    const listeners = this.events.get(event);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
    return this;
  }

  /**
   * Émet un événement et appelle tous les écouteurs enregistrés
   */
  emit(event: string, ...args: unknown[]): boolean {
    const listeners = this.events.get(event);
    if (listeners && listeners.length > 0) {
      // Créer une copie pour éviter les problèmes si un listener modifie la liste
      const listenersCopy = [...listeners];
      listenersCopy.forEach(listener => {
        try {
          listener(...args);
        } catch (error) {
          logger.error(
            `Erreur dans l'écouteur d'événement "${event}"`,
            { error, event },
            'BrowserEventEmitter'
          );
        }
      });
      return true;
    }
    return false;
  }

  /**
   * Enregistre un écouteur qui ne sera appelé qu'une seule fois
   */
  once(event: string, listener: (...args: unknown[]) => void): this {
    const onceWrapper = (...args: unknown[]) => {
      this.off(event, onceWrapper);
      listener(...args);
    };
    return this.on(event, onceWrapper);
  }

  /**
   * Supprime tous les écouteurs pour un événement spécifique
   */
  removeAllListeners(event?: string): this {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
    return this;
  }

  /**
   * Retourne le nombre d'écouteurs pour un événement
   */
  listenerCount(event: string): number {
    return this.events.get(event)?.length || 0;
  }
}
